import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { testsApi, TestAnswerPayload } from '@/api/tests';
import DynamicSurveyForm from '@/components/form/DynamicSurveyForm';
import AnswerLinkInvalid from '@/components/tests/AnswerLinkInvalid';
import AnswerSubmitted from '@/components/tests/AnswerSubmitted';
import TestApplicationInstructions from '@/components/tests/TestApplicationInstructions';
import TestPageLayout from '@/components/tests/TestPageLayout';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import type { Survey } from '@/api/surveys';

export default function AnswerTest() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [progress, setProgress] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    const {
        data: test,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['test-answer', token],
        queryFn: () => testsApi.getTestByToken(token!),
        enabled: !!token,
        retry: false,
        staleTime: Infinity,
    });

    const submitMutation = useMutation({
        mutationFn: (data: TestAnswerPayload) => testsApi.submitTestAnswer(token!, data),
        onSuccess: () => {
            setSubmitted(true);
            toast.success('Instrumento enviado com sucesso!');
        },
        onError: () => {
            toast.error('Erro ao enviar instrumento. Tente novamente.');
        },
    });

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
                <p>Token não fornecido.</p>
            </div>
        );
    }

    if (isLoading) return <Loading />;

    if (error) {
        return <AnswerLinkInvalid />;
    }

    if (!test?.sections || test.sections.length === 0) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Estrutura do instrumento indisponível
                    </h1>
                    <p className="text-muted-foreground">
                        Este instrumento ainda não possui perguntas configuradas.
                    </p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return <AnswerSubmitted />;
    }

    const testDefinition = test as unknown as Survey;

    return (
        <TestPageLayout progress={progress}>
            <div className="rounded-xl bg-card p-6 shadow-elevation-1 duration-500 animate-in slide-in-from-bottom-4 md:p-8">
                <h1 className="mb-6 border-b pb-4 text-2xl font-bold text-foreground">
                    {testDefinition?.name}
                </h1>
                <TestApplicationInstructions instructions={test?.instructions} className="mb-6" />
                <DynamicSurveyForm
                    survey={testDefinition}
                    onSubmit={(values) =>
                        submitMutation.mutate({
                            answers: values,
                            status: 'completed',
                        })
                    }
                    onProgressChange={setProgress}
                    initialData={test?.answers || {}}
                >
                    <div className="mt-8 flex justify-end">
                        <Button
                            type="submit"
                            size="lg"
                            className="w-full md:w-auto"
                            disabled={submitMutation.isPending}
                        >
                            {submitMutation.isPending ? 'Enviando...' : 'Enviar Respostas'}
                        </Button>
                    </div>
                </DynamicSurveyForm>
            </div>
        </TestPageLayout>
    );
}
